import { byteToBits } from "@/lib/tool-kit/ciphers-misc/xor";

/**
 * Binary-to-text encodings other than Base64: Base32 (RFC 4648 §6 and the extended hex
 * alphabet of §7), Base58 (the Bitcoin alphabet), Ascii85 (Adobe) and Z85 (ZeroMQ RFC 32).
 *
 * Every function works on bytes. Text goes in and out as UTF-8 elsewhere; these only
 * know bytes and characters.
 */

export type BaseEncoding =
  | "base32"
  | "base32hex"
  | "base58"
  | "ascii85"
  | "z85";

export interface EncodeOptions {
  /** Base32: end with "=" so the length is a multiple of 8. */
  padding: boolean;
  /** Ascii85: wrap the output in <~ and ~>. */
  wrap: boolean;
  /** Ascii85: write four zero bytes as the single letter z. */
  zeroShortcut: boolean;
}

export const DEFAULT_ENCODE_OPTIONS: EncodeOptions = {
  padding: true,
  wrap: true,
  zeroShortcut: true,
};

export type BaseError =
  | { kind: "characters"; invalid: string[] }
  | { kind: "length" }
  | { kind: "overflow" }
  | { kind: "z85-length"; missing: number };

export type EncodeResult =
  | { ok: true; text: string }
  | { ok: false; error: BaseError };

export type DecodeResult =
  | { ok: true; bytes: Uint8Array }
  | { ok: false; error: BaseError };

export const BASE32_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
export const BASE32_HEX_ALPHABET = "0123456789ABCDEFGHIJKLMNOPQRSTUV";
export const BASE58_ALPHABET =
  "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
export const Z85_ALPHABET =
  "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ.-:+=^!/*?&<>()[]{}@%$#";

const BASE32_BITS = 5;
const BASE32_BLOCK = 8;
const BYTE_BITS = 8;
const BYTE_VALUES = 256;
const BASE58 = 58;
const BASE85 = 85;
const GROUP_BYTES = 4;
const GROUP_CHARS = 5;
const WORD_LIMIT = 4_294_967_296;
const ASCII85_FIRST = 33;
const ASCII85_LAST = 117;
const ASCII85_PAD = ASCII85_LAST - ASCII85_FIRST;
const WHITESPACE = /\s+/g;
const ASCII85_OPEN = /^<~/;
const ASCII85_CLOSE = /~>$/;
const BASE32_PADDING = /=+$/;
const VALID_BASE32_REMAINDERS = new Set([0, 2, 4, 5, 7]);

/** Each distinct character not in `alphabet`, in order of first appearance. */
function strangers(text: string, allowed: (char: string) => boolean): string[] {
  const seen = new Set<string>();
  for (const char of text) {
    if (!allowed(char)) {
      seen.add(char);
    }
  }
  return [...seen];
}

/* ------------------------------------------------------------------ Base32 */

function base32Alphabet(encoding: BaseEncoding): string {
  return encoding === "base32hex" ? BASE32_HEX_ALPHABET : BASE32_ALPHABET;
}

export function base32Encode(
  bytes: Uint8Array,
  alphabet: string,
  padding: boolean
): string {
  const bits = [...bytes].map(byteToBits).join("");
  let text = "";
  for (let start = 0; start < bits.length; start += BASE32_BITS) {
    const chunk = bits
      .slice(start, start + BASE32_BITS)
      .padEnd(BASE32_BITS, "0");
    text += alphabet[Number.parseInt(chunk, 2)];
  }
  if (padding) {
    text = text.padEnd(
      Math.ceil(text.length / BASE32_BLOCK) * BASE32_BLOCK,
      "="
    );
  }
  return text;
}

export function base32Decode(text: string, alphabet: string): DecodeResult {
  const body = text
    .replace(WHITESPACE, "")
    .replace(BASE32_PADDING, "")
    .toUpperCase();
  const invalid = strangers(body, (char) => alphabet.includes(char));
  if (invalid.length > 0) {
    return { ok: false, error: { kind: "characters", invalid } };
  }
  if (!VALID_BASE32_REMAINDERS.has(body.length % BASE32_BLOCK)) {
    return { ok: false, error: { kind: "length" } };
  }
  const bits = [...body]
    .map((char) =>
      alphabet.indexOf(char).toString(2).padStart(BASE32_BITS, "0")
    )
    .join("");
  const bytes = new Uint8Array(Math.floor(bits.length / BYTE_BITS));
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Number.parseInt(
      bits.slice(index * BYTE_BITS, (index + 1) * BYTE_BITS),
      2
    );
  }
  return { ok: true, bytes };
}

/* ------------------------------------------------------------------ Base58 */

function leadingZeros(bytes: ArrayLike<number>): number {
  let count = 0;
  while (count < bytes.length && bytes[count] === 0) {
    count += 1;
  }
  return count;
}

/**
 * Convert between bases with schoolbook arithmetic on digit arrays (least significant
 * first), so no big-integer type is needed.
 */
function convertBase(
  digits: readonly number[] | Uint8Array,
  from: number,
  to: number
): number[] {
  const result: number[] = [];
  for (const digit of digits) {
    let carry = digit;
    for (let index = 0; index < result.length; index += 1) {
      carry += result[index] * from;
      result[index] = carry % to;
      carry = Math.floor(carry / to);
    }
    while (carry > 0) {
      result.push(carry % to);
      carry = Math.floor(carry / to);
    }
  }
  return result;
}

/** The Base58 digit values (most significant first) of the bytes, without the leading 1s. */
export function base58Digits(bytes: Uint8Array): number[] {
  return convertBase(bytes, BYTE_VALUES, BASE58).reverse();
}

export function base58Encode(bytes: Uint8Array): string {
  const zeros = leadingZeros(bytes);
  const digits = base58Digits(bytes.slice(zeros));
  return (
    BASE58_ALPHABET[0].repeat(zeros) +
    digits.map((digit) => BASE58_ALPHABET[digit]).join("")
  );
}

export function base58Decode(text: string): DecodeResult {
  const body = text.replace(WHITESPACE, "");
  const invalid = strangers(body, (char) => BASE58_ALPHABET.includes(char));
  if (invalid.length > 0) {
    return { ok: false, error: { kind: "characters", invalid } };
  }
  const values = [...body].map((char) => BASE58_ALPHABET.indexOf(char));
  const zeros = leadingZeros(values);
  const bytes = convertBase(values.slice(zeros), BASE58, BYTE_VALUES).reverse();
  return {
    ok: true,
    bytes: Uint8Array.from([...new Array(zeros).fill(0), ...bytes]),
  };
}

/* --------------------------------------------------------- Ascii85 and Z85 */

/** The 32-bit number a group of up to four bytes stands for (short groups padded with 0). */
export function groupValue(bytes: ArrayLike<number>, start: number): number {
  let value = 0;
  for (let offset = 0; offset < GROUP_BYTES; offset += 1) {
    value = value * BYTE_VALUES + (bytes[start + offset] ?? 0);
  }
  return value;
}

/** The five base-85 digits of a 32-bit number, most significant first. */
export function groupDigits(value: number): number[] {
  const digits: number[] = [];
  let rest = value;
  for (let index = 0; index < GROUP_CHARS; index += 1) {
    digits.unshift(rest % BASE85);
    rest = Math.floor(rest / BASE85);
  }
  return digits;
}

function digitsToValue(digits: number[]): number {
  return digits.reduce((total, digit) => total * BASE85 + digit, 0);
}

function valueToBytes(value: number, count: number): number[] {
  const bytes: number[] = [];
  let rest = value;
  for (let index = 0; index < GROUP_BYTES; index += 1) {
    bytes.unshift(rest % BYTE_VALUES);
    rest = Math.floor(rest / BYTE_VALUES);
  }
  return bytes.slice(0, count);
}

export function ascii85Encode(
  bytes: Uint8Array,
  wrap: boolean,
  zeroShortcut: boolean
): string {
  let text = "";
  for (let start = 0; start < bytes.length; start += GROUP_BYTES) {
    const size = Math.min(GROUP_BYTES, bytes.length - start);
    const value = groupValue(bytes, start);
    if (value === 0 && size === GROUP_BYTES && zeroShortcut) {
      text += "z";
    } else {
      const chars = groupDigits(value).map((digit) =>
        String.fromCharCode(digit + ASCII85_FIRST)
      );
      text += chars
        .slice(0, size === GROUP_BYTES ? GROUP_CHARS : size + 1)
        .join("");
    }
  }
  return wrap ? `<~${text}~>` : text;
}

function ascii85Groups(body: string): number[][] | null {
  const groups: number[][] = [];
  let current: number[] = [];
  for (const char of body) {
    if (char === "z") {
      if (current.length > 0) {
        return null;
      }
      groups.push(Array.from({ length: GROUP_CHARS }, () => 0));
    } else {
      current.push(char.charCodeAt(0) - ASCII85_FIRST);
      if (current.length === GROUP_CHARS) {
        groups.push(current);
        current = [];
      }
    }
  }
  if (current.length > 0) {
    groups.push(current);
  }
  return groups;
}

export function ascii85Decode(text: string): DecodeResult {
  const body = text
    .replace(WHITESPACE, "")
    .replace(ASCII85_OPEN, "")
    .replace(ASCII85_CLOSE, "");
  const invalid = strangers(
    body,
    (char) =>
      char === "z" ||
      (char.charCodeAt(0) >= ASCII85_FIRST &&
        char.charCodeAt(0) <= ASCII85_LAST)
  );
  if (invalid.length > 0) {
    return { ok: false, error: { kind: "characters", invalid } };
  }
  const groups = ascii85Groups(body);
  if (groups === null) {
    return { ok: false, error: { kind: "length" } };
  }
  const bytes: number[] = [];
  for (const group of groups) {
    const size = group.length;
    if (size === 1) {
      return { ok: false, error: { kind: "length" } };
    }
    const padded = [
      ...group,
      ...Array.from({ length: GROUP_CHARS - size }, () => ASCII85_PAD),
    ];
    const value = digitsToValue(padded);
    if (value >= WORD_LIMIT) {
      return { ok: false, error: { kind: "overflow" } };
    }
    bytes.push(
      ...valueToBytes(value, size === GROUP_CHARS ? GROUP_BYTES : size - 1)
    );
  }
  return { ok: true, bytes: Uint8Array.from(bytes) };
}

export function z85Encode(bytes: Uint8Array): EncodeResult {
  const remainder = bytes.length % GROUP_BYTES;
  if (remainder !== 0) {
    return {
      ok: false,
      error: { kind: "z85-length", missing: GROUP_BYTES - remainder },
    };
  }
  let text = "";
  for (let start = 0; start < bytes.length; start += GROUP_BYTES) {
    text += groupDigits(groupValue(bytes, start))
      .map((digit) => Z85_ALPHABET[digit])
      .join("");
  }
  return { ok: true, text };
}

export function z85Decode(text: string): DecodeResult {
  const body = text.replace(WHITESPACE, "");
  const invalid = strangers(body, (char) => Z85_ALPHABET.includes(char));
  if (invalid.length > 0) {
    return { ok: false, error: { kind: "characters", invalid } };
  }
  if (body.length % GROUP_CHARS !== 0) {
    return { ok: false, error: { kind: "length" } };
  }
  const bytes: number[] = [];
  for (let start = 0; start < body.length; start += GROUP_CHARS) {
    const digits = [...body.slice(start, start + GROUP_CHARS)].map((char) =>
      Z85_ALPHABET.indexOf(char)
    );
    const value = digitsToValue(digits);
    if (value >= WORD_LIMIT) {
      return { ok: false, error: { kind: "overflow" } };
    }
    bytes.push(...valueToBytes(value, GROUP_BYTES));
  }
  return { ok: true, bytes: Uint8Array.from(bytes) };
}

/* -------------------------------------------------------------- entry points */

export function encodeBytes(
  encoding: BaseEncoding,
  bytes: Uint8Array,
  options: EncodeOptions = DEFAULT_ENCODE_OPTIONS
): EncodeResult {
  switch (encoding) {
    case "base32":
    case "base32hex":
      return {
        ok: true,
        text: base32Encode(bytes, base32Alphabet(encoding), options.padding),
      };
    case "base58":
      return { ok: true, text: base58Encode(bytes) };
    case "ascii85":
      return {
        ok: true,
        text: ascii85Encode(bytes, options.wrap, options.zeroShortcut),
      };
    default:
      return z85Encode(bytes);
  }
}

export function decodeText(encoding: BaseEncoding, text: string): DecodeResult {
  switch (encoding) {
    case "base32":
    case "base32hex":
      return base32Decode(text, base32Alphabet(encoding));
    case "base58":
      return base58Decode(text);
    case "ascii85":
      return ascii85Decode(text);
    default:
      return z85Decode(text);
  }
}

/* ------------------------------------------------------------------ working */

export interface WorkingRow {
  input: string;
  value: number;
  output: string;
}

/**
 * How the start of an encoding came about, one row per 5-bit group (Base32), per
 * output digit (Base58) or per 4-byte group (Ascii85, Z85).
 */
export function workingRows(
  encoding: BaseEncoding,
  bytes: Uint8Array,
  limit: number
): WorkingRow[] {
  if (encoding === "base32" || encoding === "base32hex") {
    const alphabet = base32Alphabet(encoding);
    const bits = [...bytes].map(byteToBits).join("");
    const rows: WorkingRow[] = [];
    for (
      let start = 0;
      start < bits.length && rows.length < limit;
      start += BASE32_BITS
    ) {
      const chunk = bits
        .slice(start, start + BASE32_BITS)
        .padEnd(BASE32_BITS, "0");
      const value = Number.parseInt(chunk, 2);
      rows.push({ input: chunk, value, output: alphabet[value] });
    }
    return rows;
  }
  if (encoding === "base58") {
    return base58Digits(bytes.slice(leadingZeros(bytes)))
      .slice(0, limit)
      .map((digit) => ({
        input: "",
        value: digit,
        output: BASE58_ALPHABET[digit],
      }));
  }
  const rows: WorkingRow[] = [];
  for (
    let start = 0;
    start < bytes.length && rows.length < limit;
    start += GROUP_BYTES
  ) {
    const value = groupValue(bytes, start);
    const digits = groupDigits(value);
    const chars =
      encoding === "z85"
        ? digits.map((digit) => Z85_ALPHABET[digit])
        : digits.map((digit) => String.fromCharCode(digit + ASCII85_FIRST));
    rows.push({
      input: [...bytes.slice(start, start + GROUP_BYTES)]
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join(" "),
      value,
      output: chars.join(""),
    });
  }
  return rows;
}
